/* Bedrock cart drawer.
 *
 * Replaces every cart on the site. These pages are layout-none clones, so they
 * carry Onset's OWN cart drawer and its scripts, and the Elixir theme has a
 * third one. Three carts fighting over the same /cart/add.js is exactly the
 * conflict Adam asked to avoid, so this file does two things:
 *
 *   1. Neutralises the inherited drawers: their markup is removed from the DOM
 *      and any click that used to open them is captured and re-pointed here.
 *   2. Renders our own drawer from /cart.js, which is the single source of truth.
 *
 * SHIPPING PROTECTION is pre-checked by default. On first open, if the cart has
 * lines and no protection line, it is added automatically. Unchecking removes it
 * and remembers that choice for the session, so it does not silently re-add
 * itself after a customer has declined — that behaviour is what gets a store
 * accused of sneaking charges in.
 */
(function () {
  'use strict';

  var CFG = window.BEDROCK_CART || {};
  var PROTECT_ID = CFG.protectionVariantId || null;
  var PROTECT_PRICE = CFG.protectionPrice || 4.95;
  var FREE_SHIP_AT = (CFG.freeShippingThreshold != null ? CFG.freeShippingThreshold : 75) * 100;
  var UPSELLS = CFG.upsells || [];
  var DECLINED_KEY = 'bdc_protect_declined';

  var money = function (c) { return '$' + (c / 100).toFixed(2); };
  var declined = function () { try { return sessionStorage.getItem(DECLINED_KEY) === '1'; } catch (e) { return false; } };
  var setDeclined = function (v) { try { v ? sessionStorage.setItem(DECLINED_KEY, '1') : sessionStorage.removeItem(DECLINED_KEY); } catch (e) {} };

  var root, cart = null, busy = false;

  /* ---------- kill the inherited carts ---------------------------------- */

  function purgeLegacyCarts() {
    var SEL = [
      '.ua-cart-drawer', '.ua-ax-cart-drawer', '#CartDrawer', 'cart-drawer',
      '.cart-drawer', '.drawer--cart', '#cart-drawer', '.js-cart-drawer',
      '.ua-cart-drawer-overlay', '.cart-drawer__overlay'
    ];
    SEL.forEach(function (s) {
      document.querySelectorAll(s).forEach(function (el) {
        if (root && (el === root || el.contains(root))) return;
        el.remove();
      });
    });
  }

  /* Capture-phase click handler. Anything that looks like a cart trigger opens
   * OUR drawer and never reaches the inherited handler. Checkout links are left
   * alone so a customer can always escape to /checkout. */
  function interceptTriggers() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest(
        'a[href="/cart"], a[href$="/cart"], .ua-cart-icon, .ua-header-cart, ' +
        '[data-cart-toggle], .js-cart-toggle, #cart-icon-bubble, .header__icon--cart, ' +
        '.ua-ax-cart, .cart-count-bubble'
      );
      if (!a) return;
      if (/\/checkout/.test(a.getAttribute('href') || '')) return;
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);
  }

  /* ---------- data ------------------------------------------------------ */

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }

  function change(line, quantity) {
    busy = true; render();
    return fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (r) { return r.json(); })
      .then(function (c) { cart = c; busy = false; return sync(); });
  }

  function addItems(items) {
    busy = true; render();
    return fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function () { return fetchCart(); })
      .then(function (c) { cart = c; busy = false; return sync(); });
  }

  function protectionLine() {
    if (!cart || !PROTECT_ID) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === String(PROTECT_ID)) {
        return { item: cart.items[i], line: i + 1 };
      }
    }
    return null;
  }

  /* Real product lines, i.e. not the protection add-on and not the $0 gifts.
   * The free-shipping meter and the item count should both ignore them. */
  function realItems() {
    if (!cart) return [];
    return cart.items.filter(function (i) {
      return String(i.variant_id) !== String(PROTECT_ID) && i.price > 0;
    });
  }

  /* Pre-check: add protection unless the customer has explicitly declined. */
  function sync() {
    if (!PROTECT_ID || !cart) return render();
    var has = protectionLine();
    var wants = !declined() && realItems().length > 0;
    if (wants && !has) return addItems([{ id: PROTECT_ID, quantity: 1 }]);
    if (!wants && has && realItems().length === 0) return change(has.line, 0);
    return render();
  }

  /* ---------- render ---------------------------------------------------- */

  var TRUCK = '<svg viewBox="0 0 24 24" fill="none" stroke="#111" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M1 3h12v11H1z"/><path d="M13 7h4l3 3v4h-7z"/><circle cx="5.5" cy="17.5" r="1.8"/><circle cx="16.5" cy="17.5" r="1.8"/></svg>';

  function lineHTML(item, idx) {
    var opts = (item.options_with_values || []).filter(function (o) {
      return o.value && !/default/i.test(o.value);
    }).map(function (o) {
      return '<p class="bdc-line-opt">' + esc(o.name) + ': ' + esc(o.value) + '</p>';
    }).join('');

    var was = item.original_line_price > item.final_line_price
      ? '<span class="bdc-was">' + money(item.original_line_price) + '</span>' : '';
    var saved = item.original_line_price - item.final_line_price;
    var save = saved > 0 ? '<div class="bdc-save">(Save ' + money(saved) + ')</div>' : '';
    var tags = (item.discounts || []).map(function (d) {
      return '<span class="bdc-tag">' + esc(d.title) + '</span>';
    }).join('');

    return '<div class="bdc-line" data-line="' + (idx + 1) + '">' +
      '<img class="bdc-line-img" src="' + (item.image ? esc(item.image) : '') + '" alt="">' +
      '<div class="bdc-line-main">' +
        '<div class="bdc-line-top">' +
          '<div><h3 class="bdc-line-title">' + esc(item.product_title) + '</h3>' + opts + '</div>' +
          '<button class="bdc-remove" data-remove="' + (idx + 1) + '" aria-label="Remove">' +
            '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="bdc-line-bottom">' +
          '<div class="bdc-qty">' +
            '<button data-qty="' + (idx + 1) + '" data-delta="-1">&minus;</button>' +
            '<span>' + item.quantity + '</span>' +
            '<button data-qty="' + (idx + 1) + '" data-delta="1">+</button>' +
          '</div>' +
          '<div class="bdc-price">' + was + '<span class="bdc-now">' + money(item.final_line_price) + '</span>' +
            save + tags + '</div>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function upsellHTML() {
    var inCart = {};
    (cart ? cart.items : []).forEach(function (i) { inCart[String(i.product_id)] = true; });
    var avail = UPSELLS.filter(function (u) { return !inCart[String(u.productId)]; });
    if (!avail.length) return '';
    return '<h4 class="bdc-ups-head">Customers also bought:</h4>' + avail.map(function (u) {
      var was = u.compareAt ? '<span class="bdc-ups-was">' + u.compareAt + '</span>' : '';
      var sel = (u.variants || []).map(function (v) {
        return '<option value="' + v.id + '">' + esc(v.title) + '</option>';
      }).join('');
      return '<div class="bdc-ups">' +
        '<img class="bdc-ups-img" src="' + esc(u.image || '') + '" alt="">' +
        '<div class="bdc-ups-main">' +
          '<h5 class="bdc-ups-title">' + esc(u.title) + '</h5>' +
          '<p class="bdc-ups-price">' + was + '<b>' + u.price + '</b></p>' +
          '<div class="bdc-ups-row">' +
            (sel ? '<select data-ups-sel="' + u.productId + '">' + sel + '</select>' : '') +
            '<button class="bdc-ups-add" data-ups-add="' + u.productId + '">Add +</button>' +
          '</div>' +
        '</div></div>';
    }).join('');
  }

  function render() {
    if (!root) return;
    var body = root.querySelector('.bdc-body');
    var foot = root.querySelector('.bdc-foot');
    var count = realItems().reduce(function (n, i) { return n + i.quantity; }, 0);
    root.querySelector('.bdc-title').textContent = 'Cart • ' + count;
    root.querySelector('.bdc-panel').classList.toggle('bdc-busy', busy);

    if (!cart || !cart.items.length) {
      body.innerHTML = '<div class="bdc-empty">Your cart is empty.</div>';
      foot.innerHTML = '';
      return;
    }

    var goods = realItems().reduce(function (n, i) { return n + i.final_line_price; }, 0);
    var away = Math.max(0, FREE_SHIP_AT - goods);
    var pct = Math.min(100, FREE_SHIP_AT ? (goods / FREE_SHIP_AT) * 100 : 100);
    var ship = '<div class="bdc-ship' + (away === 0 ? ' is-hit' : '') + '">' +
      '<p class="bdc-ship-msg">' + (away === 0
        ? 'Your order ships <b>free</b>.'
        : 'You\'re <b>' + money(away) + '</b> away from free shipping!') + '</p>' +
      '<div class="bdc-ship-track"><div class="bdc-ship-fill" style="width:' + pct + '%"></div>' +
        '<div class="bdc-ship-badge">' + TRUCK + '</div></div>' +
      '<div class="bdc-ship-label">Free Shipping</div></div>';

    var lines = cart.items.map(function (item, i) {
      return String(item.variant_id) === String(PROTECT_ID) ? '' : lineHTML(item, i);
    }).join('');

    var protect = PROTECT_ID ? '<label class="bdc-protect">' +
      '<input type="checkbox" id="bdc-protect"' + (protectionLine() ? ' checked' : '') + '>' +
      '<span class="bdc-protect-copy">' +
        '<span class="bdc-protect-title">Shipping Protection</span>' +
        '<p class="bdc-protect-sub">Covers loss, theft or damage in transit. We replace it, no questions asked.</p>' +
      '</span>' +
      '<span class="bdc-protect-price">' + money(PROTECT_PRICE * 100) + '</span></label>' : '';

    body.innerHTML = ship + lines + protect + upsellHTML();

    var disc = (cart.cart_level_discount_applications || []).concat([]);
    var saved = cart.items.reduce(function (n, i) { return n + (i.original_line_price - i.final_line_price); }, 0);
    var discRow = saved > 0
      ? '<div class="bdc-disc"><span class="bdc-disc-left">Discounts' +
        (disc[0] ? '<span class="bdc-tag">' + esc(disc[0].title) + '</span>' : '') +
        '</span><span class="bdc-disc-amt">- ' + money(saved) + '</span></div>' : '';

    foot.innerHTML = discRow +
      '<button class="bdc-checkout" id="bdc-checkout">Checkout • ' + money(cart.total_price) + '</button>';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- wiring ---------------------------------------------------- */

  function open() {
    root.classList.add('bdc-open');
    document.documentElement.style.overflow = 'hidden';
    fetchCart().then(function (c) { cart = c; return sync(); });
  }
  function close() {
    root.classList.remove('bdc-open');
    document.documentElement.style.overflow = '';
  }

  function mount() {
    root = document.createElement('div');
    root.className = 'bdc-root';
    root.innerHTML =
      '<div class="bdc-scrim" data-bdc-close></div>' +
      '<aside class="bdc-panel" role="dialog" aria-label="Cart">' +
        '<div class="bdc-head"><h2 class="bdc-title">Cart</h2>' +
          '<button class="bdc-close" data-bdc-close aria-label="Close">&#10005;</button></div>' +
        '<div class="bdc-body"></div><div class="bdc-foot"></div>' +
      '</aside>';
    document.body.appendChild(root);

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-bdc-close]')) return close();

      var q = t.closest('[data-qty]');
      if (q && !busy) {
        var line = +q.getAttribute('data-qty'), d = +q.getAttribute('data-delta');
        var it = cart.items[line - 1];
        return change(line, Math.max(0, it.quantity + d));
      }
      var rm = t.closest('[data-remove]');
      if (rm && !busy) return change(+rm.getAttribute('data-remove'), 0);

      var add = t.closest('[data-ups-add]');
      if (add && !busy) {
        var pid = add.getAttribute('data-ups-add');
        var sel = root.querySelector('[data-ups-sel="' + pid + '"]');
        var vid = sel ? sel.value : (UPSELLS.find(function (u) { return String(u.productId) === pid; }) || {}).defaultVariantId;
        if (vid) { add.disabled = true; return addItems([{ id: +vid, quantity: 1 }]); }
      }
      if (t.id === 'bdc-checkout') { window.location.href = '/checkout'; }
    });

    root.addEventListener('change', function (e) {
      if (e.target.id !== 'bdc-protect' || busy) return;
      var has = protectionLine();
      if (e.target.checked) { setDeclined(false); if (!has) addItems([{ id: PROTECT_ID, quantity: 1 }]); }
      else { setDeclined(true); if (has) change(has.line, 0); }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('bdc-open')) close();
    });
  }

  function boot() {
    mount();
    purgeLegacyCarts();
    interceptTriggers();
    // Re-purge after the cloned scripts finish, since some re-inject their drawer.
    setTimeout(purgeLegacyCarts, 800);
    setTimeout(purgeLegacyCarts, 2500);

    /* Every existing add-to-cart on these pages posts to /cart/add.js and then
     * sends the buyer to /checkout. Intercept that navigation so the drawer
     * opens instead: the upsells and the protection add-on are worth more than
     * a straight jump to checkout. */
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = _fetch.apply(this, arguments);
      if (/\/cart\/(add|change|update)\.js/.test(url)) {
        p.then(function () { fetchCart().then(function (c) { cart = c; sync(); open(); }); }).catch(function () {});
      }
      return p;
    };

    window.BedrockCart = { open: open, close: close, refresh: function () { return fetchCart().then(function (c) { cart = c; return sync(); }); } };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
